const BASE = import.meta.env.VITE_API_URL || "http://localhost:4000";

// Parse the `Retry-After` header (seconds or HTTP-date). Returns 60 if the
// value is missing or unparseable so the gate always shows a sensible
// countdown.
function parseRetryAfter(headers: Headers): number {
  const raw = headers.get("Retry-After");
  if (raw) {
    const asNum = Number(raw);
    if (Number.isFinite(asNum) && asNum > 0) return Math.ceil(asNum);
    const asDate = Date.parse(raw);
    if (!Number.isNaN(asDate)) {
      return Math.max(1, Math.ceil((asDate - Date.now()) / 1000));
    }
  }
  return 60;
}

// Wrap a thrown API error envelope in a real Error so Sentry captures a stack
// trace. Consumers still read `.status` / `.error` as before. On 429 we also
// dispatch a global event picked up by <RateLimitGate>, which blocks the UI
// behind a countdown until the limit clears.
function buildApiError(
  status: number,
  body: Record<string, unknown>,
  headers: Headers,
): Error & { status: number } {
  const message =
    typeof body.error === "string"
      ? `API ${status} ${body.error}`
      : `API ${status}`;
  if (status === 429) {
    window.dispatchEvent(
      new CustomEvent("ss:rate-limited", {
        detail: { retryAfter: parseRetryAfter(headers) },
      }),
    );
  }
  return Object.assign(new Error(message), { ...body, status });
}

export async function apiPost<T = unknown>(
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw buildApiError(res.status, data, res.headers);
  return data as T;
}

export async function apiGet<T = unknown>(path: string): Promise<T> {
  // `cache: "no-store"` skips the browser HTTP cache so the server never sends
  // back a 304 with an empty body (which `res.json()` would choke on, leaving
  // the calling query stuck in loading). React Query handles caching for us.
  const res = await fetch(`${BASE}${path}`, {
    credentials: "include",
    cache: "no-store",
  });
  const data = await res.json();
  if (!res.ok) throw buildApiError(res.status, data, res.headers);
  return data as T;
}

export async function apiPut<T = unknown>(
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PUT",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw buildApiError(res.status, data, res.headers);
  return data as T;
}

export async function apiPatch<T = unknown>(
  path: string,
  body: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "PATCH",
    headers: { "Content-Type": "application/json" },
    credentials: "include",
    body: JSON.stringify(body),
  });
  const data = await res.json();
  if (!res.ok) throw buildApiError(res.status, data, res.headers);
  return data as T;
}

export async function apiDelete<T = unknown>(
  path: string,
  body?: unknown,
): Promise<T> {
  const res = await fetch(`${BASE}${path}`, {
    method: "DELETE",
    credentials: "include",
    ...(body !== undefined
      ? {
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(body),
        }
      : {}),
  });
  const data = await res.json();
  if (!res.ok) throw buildApiError(res.status, data, res.headers);
  return data as T;
}

// Pulls the API error code out of a thrown apiPost/apiGet rejection.
// Returns "INTERNAL_ERROR" for anything that doesn't look like our error envelope.
export function getApiErrorCode(err: unknown): string {
  if (err && typeof err === "object" && "error" in err) {
    const code = (err as { error?: unknown }).error;
    if (typeof code === "string") return code;
  }
  return "INTERNAL_ERROR";
}
